import { INotification, notificationEnums } from "@freetech/models/notifications";

/**
 * Determines the destination URL when a notification is clicked
 * @param notification The notification object
 * @returns The URL to navigate to, or undefined if no navigation should occur
 */
export const handleNotificationClick = (
  notification: INotification
): string | undefined => {
  const { type, afterData } = notification;

  if (Object.values(notificationEnums.StakeholderNotificationType).includes(type as any)) {
    switch (type) {
      case notificationEnums.StakeholderNotificationType.STAKEHOLDER_INVOICE_RECIEVED:
      case notificationEnums.StakeholderNotificationType.STAKEHOLDER_INVOICE_PAID:
        return undefined;
      case notificationEnums.StakeholderNotificationType.STAKEHOLDER_PAYMENT_CREATED:
      case notificationEnums.StakeholderNotificationType.STAKEHOLDER_PAYMENT_COMPLETED:
      case notificationEnums.StakeholderNotificationType.STAKEHOLDER_PAYMENT_FAILED:
      case notificationEnums.StakeholderNotificationType.STAKEHOLDER_PAYMENT_REVERSED:
        return "/payments";
      case notificationEnums.StakeholderNotificationType.STAKEHOLDER_CSA_RECIEVED:
        return "/csa";
    }
  }

  if (Object.values(notificationEnums.FreeloNotificationType).includes(type as any)) {
    switch (type) {
      case notificationEnums.FreeloNotificationType.FREELO_CARD_CREATED:
      case notificationEnums.FreeloNotificationType.FREELO_CARD_UPDATED:
      case notificationEnums.FreeloNotificationType.FREELO_CARD_DELETED:
      case notificationEnums.FreeloNotificationType.FREELO_CARD_ASSIGNED:
      case notificationEnums.FreeloNotificationType.FREELO_CARD_COMMENT_MENTION:
      case notificationEnums.FreeloNotificationType.FREELO_CARD_STATUS_CHANGED:
      case notificationEnums.FreeloNotificationType.FREELO_DEADLINE_APPROACHING:
        if (afterData && 'boardId' in afterData && 'humanReadableId' in afterData) {
          return `/freelo/${afterData.boardId}#${afterData.humanReadableId}`;
        }
        return "/freelo";
    }
  }

  if (Object.values(notificationEnums.AdminNotificationType).includes(type as any)) {
    switch (type) {
      case notificationEnums.AdminNotificationType.ADMIN_USER_CREATED:
      case notificationEnums.AdminNotificationType.ADMIN_USER_SIGNED_UP:
      case notificationEnums.AdminNotificationType.ADMIN_USER_ONBOARDING_COMPLETE:
        if (afterData && 'id' in afterData) {
          return `/admin/users/${afterData.id}`;
        }
        return "/admin/users";

      case notificationEnums.AdminNotificationType.ADMIN_CLIENT_CREATED:
      case notificationEnums.AdminNotificationType.ADMIN_CLIENT_UPDATED:
        if (afterData && 'id' in afterData) {
          return `/admin/clients/${afterData.id}`;
        }
        return "/admin/clients";

      case notificationEnums.AdminNotificationType.ADMIN_PROJECT_CREATED:
      case notificationEnums.AdminNotificationType.ADMIN_PROJECT_UPDATED:
        if (afterData && 'id' in afterData) {
          return `/admin/projects/${afterData.id}`;
        }
        return "/admin/projects";

      case notificationEnums.AdminNotificationType.ADMIN_CSA_CREATED:
      case notificationEnums.AdminNotificationType.ADMIN_CSA_SIGNED_BY_ADMIN:
      case notificationEnums.AdminNotificationType.ADMIN_CSA_SIGNED_BY_STAKEHOLDER:
        if (afterData && 'id' in afterData) {
          return `/admin/csa/${afterData.id}`;
        }
        return "/admin/csa";

      case notificationEnums.AdminNotificationType.ADMIN_INVOICE_CREATED:
      case notificationEnums.AdminNotificationType.ADMIN_INVOICE_PAID:
        if (afterData && 'id' in afterData) {
          return `/admin/invoices/${afterData.id}`;
        }
        return "/admin/invoices";

      case notificationEnums.AdminNotificationType.ADMIN_PAYMENT_CREATED:
      case notificationEnums.AdminNotificationType.ADMIN_PAYMENT_STATUS_CHANGED:
      case notificationEnums.AdminNotificationType.ADMIN_PAYMENT_COMPLETED:
        if (afterData && 'id' in afterData) {
          return `/admin/payments/${afterData.id}`;
        }
        return "/admin/payments";

      case notificationEnums.AdminNotificationType.ADMIN_PROJECT_ASSIGNMENT_CREATED:
      case notificationEnums.AdminNotificationType.ADMIN_PROJECT_ASSIGNMENT_SIGNED:
        if (afterData && 'id' in afterData) {
          return `/admin/project-assignments/${afterData.id}`;
        }
        return "/admin/project-assignments";

      case notificationEnums.AdminNotificationType.ADMIN_TIMESHEET_CREATED:
      case notificationEnums.AdminNotificationType.ADMIN_TIMESHEET_SUBMITTED:
      case notificationEnums.AdminNotificationType.ADMIN_TIMESHEET_APPROVED:
      case notificationEnums.AdminNotificationType.ADMIN_TIMESHEET_REJECTED:
        if (afterData && 'id' in afterData) {
          return `/admin/timesheets/${afterData.id}`;
        }
        return "/admin/timesheets";
    }
  }

  if (Object.values(notificationEnums.FreelancerNotificationType).includes(type as any)) {
    switch (type) {
      case notificationEnums.FreelancerNotificationType.FREELANCER_PAYMENT_CREATED:
      case notificationEnums.FreelancerNotificationType.FREELANCER_PAYMENT_UPDATED:
        if (afterData && 'id' in afterData) {
          return `/payments/${afterData.id}`;
        }
        return "/payments";

      case notificationEnums.FreelancerNotificationType.FREELANCER_PROJECT_ASSIGNMENT_CREATED:
      case notificationEnums.FreelancerNotificationType.FREELANCER_PROJECT_ASSIGNMENT_COMPLETED:
        if (afterData && 'id' in afterData) {
          return `/assignments/${afterData.id}`;
        }
        return "/assignments";

      case notificationEnums.FreelancerNotificationType.FREELANCER_AGREEMENT_CREATED:
      case notificationEnums.FreelancerNotificationType.FREELANCER_AGREEMENT_REMINDER:
        if (afterData && 'id' in afterData) {
          return `/agreements/${afterData.id}`;
        }
        return "/agreements";

      case notificationEnums.FreelancerNotificationType.FREELANCER_ONBOARDING_COMPLETE:
        return "/dashboard";
    }
  }

  return undefined;
};
